/**
 * layoutCatalogoExterno.js
 *
 * Única fuente de verdad del orden, el encabezado y el tipo de celda de las 74
 * columnas de la hoja "CATALOGO": el padrón de empleados en el layout del
 * software de nómina externo del cliente.
 *
 * Es la SALIDA del mismo layout que
 * `Pages/Panel/Empleados/ImportarEmpleadosExterno/mapeoExterno.js` sabe leer.
 * Las codificaciones (Sexo M/F, Base de cotización F/V/M, Estatus A/B/R, Estado
 * Civil, clave RENAPO de 2 letras para la entidad de nacimiento) se aplican en
 * el backend — `erp_backend/Controllers/reportesNomina.controllers.js`, que es
 * quien tiene los catálogos a la mano — y aquí sólo se acomodan en su columna.
 *
 * Dos rarezas del layout que se reproducen a propósito, para no romper a quien
 * ya consume este archivo:
 *   · La columna A dice "Registro Patronal" pero lo que trae es el CÓDIGO del
 *     empleado. El registro patronal del IMSS de verdad va en la columna BF.
 *   · No hay columna "Nombre": el layout sólo trae los dos apellidos.
 *
 * `clave` es la propiedad del renglón que devuelve el backend. `constante` son
 * las columnas que el ERP no tiene: se emiten con el valor neutro del layout
 * (0, vacío, o 30/12/1899 que es su fecha nula) igual que en el archivo original.
 */

// 30/12/1899 es la fecha cero de Excel, que es como el layout externo escribe
// una fecha vacía (ver FECHAS_CENTINELA en mapeoExterno.js).
const FECHA_NULA_EXTERNA = "30/12/1899";

export const COLUMNAS_CATALOGO = [
  { columna: "Registro Patronal", clave: "codigo", tipo: "texto" },
  { columna: "Fecha de alta", clave: "fechaAlta", tipo: "texto" },
  { columna: "Fecha de baja", clave: "fechaBaja", tipo: "texto" },
  { columna: "Fecha de reingreso", clave: "fechaReingreso", tipo: "texto" },
  { columna: "Tipo de contrato", clave: "tipoContrato", tipo: "texto" },
  { columna: "Apellido paterno", clave: "apellidoPaterno", tipo: "texto" },
  { columna: "Apellido materno", clave: "apellidoMaterno", tipo: "texto" },
  { columna: "Puesto", clave: "puesto", tipo: "texto" },
  { columna: "Salario diario", clave: "salarioDiario", tipo: "moneda" },
  { columna: "SBC parte fija", clave: "sbcParteFija", tipo: "moneda" },
  { columna: "Tipo de periodo", clave: "tipoPeriodo", tipo: "texto" },
  { columna: "Base de cotización", clave: "baseDeCotizacion", tipo: "texto" },
  { columna: "Estatus empleado", clave: "estatusEmpleado", tipo: "texto" },
  { columna: "Departamento", clave: "departamento", tipo: "texto" },
  { columna: "Num seguridad social", clave: "numSeguridadSocial", tipo: "texto" },
  { columna: "RFC", clave: "rfc", tipo: "texto" },
  { columna: "CURP", clave: "curp", tipo: "texto" },
  { columna: "Sindicalizado", clave: "sindicalizado", tipo: "texto" },
  { columna: "Afore", clave: "afore", tipo: "numero" },
  { columna: "Expediente", clave: "expediente", tipo: "texto" },
  { columna: "Sexo", clave: "sexo", tipo: "texto" },
  { columna: "Ciudad de nacimiento", clave: "ciudadNacimiento", tipo: "texto" },
  { columna: "Fecha de nacimiento", clave: "fechaNacimiento", tipo: "texto" },
  { columna: "UMF", clave: "umf", tipo: "texto" },
  { columna: "Nombre del padre", clave: "nombrePadre", tipo: "texto" },
  { columna: "Nombre de la madre", clave: "nombreMadre", tipo: "texto" },
  { columna: "Dirección", clave: "direccion", tipo: "texto" },
  { columna: "Población", clave: "poblacion", tipo: "texto" },
  { columna: "Entidad federativa de domicilio", clave: "entidadDomicilio", tipo: "texto" },
  { columna: "CP", clave: "cp", tipo: "texto" },
  { columna: "Estado Civil", clave: "estadoCivil", tipo: "texto" },
  { columna: "Teléfono", clave: "telefono", tipo: "texto" },
  // Los 4 encabezados de aviso terminan con un espacio en el archivo original.
  // Se conserva: mapeoExterno.js normaliza encabezados, así que el round-trip
  // funciona igual, pero quien compare el archivo columna por columna lo espera.
  { columna: "Aviso pendiente modificación SBC ", clave: "avisoModifSbc", tipo: "texto" },
  { columna: "Aviso pendiente reingreso IMSS ", clave: "avisoReingreso", tipo: "texto" },
  { columna: "Aviso pendiente baja IMSS ", clave: "avisoBaja", tipo: "texto" },
  { columna: "Aviso pendiente cambio base cotización ", clave: "avisoCambioBaseCotizacion", tipo: "texto" },
  { columna: "Fecha del salario diario", clave: "fechaSalarioDiario", tipo: "texto" },
  { columna: "Fecha SBC parte fija", clave: "fechaSbcParteFija", tipo: "texto" },
  { columna: "Salario variable", clave: "salarioVariable", tipo: "moneda" },
  { columna: "Fecha salario variable", clave: "fechaSalarioVariable", tipo: "texto" },
  { columna: "Salario promedio", clave: "salarioPromedio", tipo: "moneda" },
  { columna: "Fecha salario promedio", clave: "fechaSalarioPromedio", tipo: "texto" },
  { columna: "Salario base liquidación", clave: "salarioBaseLiquidacion", tipo: "moneda" },
  { columna: "Saldo del ajuste al neto", clave: "saldoAjusteNeto", tipo: "moneda" },
  { columna: "Cálculo PTU", clave: "calculoPTU", tipo: "texto" },
  { columna: "Cálculo aguinaldo", clave: "calculoAguinaldo", tipo: "texto" },
  { columna: "Banco para pago electrónico", clave: "banco", tipo: "texto" },
  { columna: "Numero de cuenta para pago electrónico", clave: "numCuenta", tipo: "texto" },
  { columna: "Sucursal para pago electrónico", clave: "sucursalPagoElectronico", tipo: "texto" },
  { columna: "Causa de la última baja", clave: "causaUltimaBaja", tipo: "texto" },
  { columna: "Campo extra numérico 1", clave: "numExtra1", tipo: "numero" },
  { columna: "Campo extra numérico 2", constante: "0", tipo: "texto" },
  { columna: "Campo extra numérico 3", constante: "0", tipo: "texto" },
  { columna: "Campo extra numérico 4", constante: "0", tipo: "texto" },
  { columna: "Campo extra numérico 5", constante: "0", tipo: "texto" },
  { columna: "Fecha salario mixto", constante: FECHA_NULA_EXTERNA, tipo: "texto" },
  { columna: "Salario mixto", constante: "0", tipo: "texto" },
  { columna: "Registro patronal del IMSS", clave: "registroPatronalIMSS", tipo: "texto" },
  { columna: "Número FONACOT", clave: "numeroFONACOT", tipo: "numero" },
  { columna: "Correo electrónico", clave: "correoElectronico", tipo: "texto" },
  { columna: "Tipo de régimen", clave: "tipoRegimen", tipo: "texto" },
  { columna: "CLABE interbancaria", clave: "clabeInterbancaria", tipo: "texto" },
  { columna: "Entidad federativa de nacimiento", clave: "entidadNacimiento", tipo: "texto" },
  { columna: "Tipo de prestación", clave: "tipoPrestacion", tipo: "texto" },
  { columna: "Extranjero sin CURP", clave: "extranjeroSinCurp", tipo: "texto" },
  // De aquí abajo, columnas del sistema externo que el ERP no maneja
  // (COLUMNAS_NO_MAPEABLES en mapeoExterno.js). Salen con su valor neutro.
  { columna: "Tipo de semana reducida", constante: "0", tipo: "texto" },
  { columna: "Dias vacaciones tomadas antes de alta", constante: "0", tipo: "texto" },
  { columna: "Dias prima vacacional tomadas antes de alta", constante: "0", tipo: "texto" },
  { columna: "Teletrabajador", constante: "0", tipo: "texto" },
  { columna: "Equipo", constante: "", tipo: "texto" },
  { columna: "Insumo", constante: "", tipo: "texto" },
  { columna: "DireccionTeletrabajo", constante: "", tipo: "texto" },
  { columna: "Líder", clave: "lider", tipo: "texto" },
  { columna: "Empleado en Colabora", constante: "0", tipo: "texto" },
];

export const HEADERS_CATALOGO = COLUMNAS_CATALOGO.map((c) => c.columna);

// Ancho por tipo, para no listar 74 números a mano.
export const ANCHOS_CATALOGO = COLUMNAS_CATALOGO.map((c) => {
  if (c.columna.length > 30) return 30;
  return Math.max(12, Math.min(c.columna.length + 2, 26));
});
